import '../../i18n/autoTranslate';
import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import SpotlightCard from '../../../components/ui/SpotlightCard';

const AnswerReviewSection = ({ 
  questions = [
    {
      id: 1,
      question: "Tell me about a challenging project you worked on and how you overcame the obstacles.",
      userAnswer: `I worked on a complex e-commerce platform migration project last year. The main challenge was migrating legacy data while maintaining zero downtime. I collaborated with the DevOps team to implement a blue-green deployment strategy and created comprehensive rollback procedures. The project was completed successfully with minimal disruption to users.`,
      aiModelAnswer: `I'd like to share an experience from my role as a Senior Developer at TechCorp. We were tasked with migrating our legacy e-commerce platform to a modern microservices architecture within a tight 3-month deadline.\n\nThe main challenge was ensuring zero downtime during the migration while handling 100,000+ daily transactions. I took the initiative to research and propose a blue-green deployment strategy.\n\nI collaborated closely with our DevOps team to implement automated testing pipelines and created comprehensive rollback procedures. I also conducted daily stand-ups to track progress and address blockers immediately.\n\nAs a result, we completed the migration 2 weeks ahead of schedule with zero downtime and improved system performance by 40%. This experience taught me the importance of thorough planning and cross-team collaboration.`,
      score: 78,
      highlights: {
        strengths: ["Clear problem identification", "Mentioned specific solution", "Collaborative approach"],
        improvements: ["Could use STAR method more explicitly", "Add quantifiable results", "Include lessons learned"]
      },
      audioUrl: "/answer-1.mp3",
      duration: "2:34"
    },
    {
      id: 2,
      question: "Describe a time when you had to work with a difficult team member.",
      userAnswer: `There was a situation where a team member was consistently missing deadlines and not communicating well. I approached them privately to understand their challenges and offered to help. We worked together to create a better workflow and improved communication. Eventually, the situation improved and we delivered the project successfully.`,
      aiModelAnswer: `In my previous role, I encountered a situation where a talented developer on our team was struggling with communication and missing critical deadlines, affecting our sprint goals.\n\nI was responsible for ensuring our team met the quarterly release deadline, so I needed to address this constructively. I scheduled a private one-on-one meeting to understand their perspective and challenges.\n\nI discovered they were overwhelmed with personal issues and felt disconnected from the team. I worked with them to restructure their workload, implemented daily check-ins, and connected them with our employee assistance program. I also facilitated better integration with team activities.\n\nWithin a month, their performance improved significantly, and we not only met our deadline but delivered ahead of schedule. This experience reinforced my belief in addressing issues with empathy and finding root causes rather than just symptoms.`,
      score: 65,
      highlights: {
        strengths: ["Showed empathy", "Took initiative", "Positive outcome"],
        improvements: ["More specific about the situation", "Quantify the impact", "Elaborate on the approach taken"]
      },
      audioUrl: "/answer-2.mp3",
      duration: "1:58"
    }
  ],
  isLoading = false
}) => {
  const [selectedQuestion, setSelectedQuestion] = useState(0);
  const [showComparison, setShowComparison] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  const handlePlayAudio = () => {
    setIsPlaying(!isPlaying);
    // Mock audio play functionality
    if (!isPlaying) {
      setTimeout(() => setIsPlaying(false), 3000);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-indigo-400';
    return 'text-yellow-400';
  };

  if (isLoading) {
    return (
      <SpotlightCard spotlightColor="rgba(139, 92, 246, 0.15)">
        <div className="bg-gradient-to-br from-[rgba(30,30,46,0.9)] via-[rgba(30,30,46,0.8)] to-[rgba(46,46,69,0.5)] border border-white/10  p-6 animate-pulse">
          <div className="space-y-6">
            <div className="h-6 bg-gray-700/50 rounded w-48"></div>
            <div className="flex space-x-4">
              {[1, 2]?.map(i => (
                <div key={i} className="h-10 bg-gray-700/50 rounded w-32"></div>
              ))}
            </div>
            <div className="space-y-4">
              <div className="h-4 bg-gray-700/50 rounded w-full"></div>
              <div className="h-20 bg-gray-700/50 rounded"></div>
            </div>
          </div>
        </div>
      </SpotlightCard>
    );
  }

  const currentQuestion = questions?.[selectedQuestion];

  return (
    <SpotlightCard spotlightColor="rgba(139, 92, 246, 0.15)">
      <div className="bg-gradient-to-br from-[rgba(30,30,46,0.9)] via-[rgba(30,30,46,0.8)] to-[rgba(46,46,69,0.5)] border border-white/10  p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-white">Answer Review</h3>
            <p className="text-sm text-gray-400 mt-1">
              Compare your responses with AI-optimized model answers
            </p>
          </div>
          <Icon name="PlayCircle" size={24} className="text-indigo-400" />
        </div>

        {/* Question Selector */}
        <div className="flex flex-wrap gap-2 mb-6">
          {questions?.map((_, index) => (
            <button
              key={index}
              onClick={() => setSelectedQuestion(index)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                selectedQuestion === index
                  ? 'bg-indigo-500 text-white shadow-lg'
                  : 'bg-gray-800/50 text-gray-400 hover:bg-gray-700/50 hover:text-white border border-gray-700'
              }`}
            >
              Question {index + 1}
            </button>
          ))}
        </div>

        {/* Question Display */}
        <div className="mb-6">
          <div className="flex items-start space-x-3 p-4 bg-gray-800/30 rounded-lg border border-gray-700">
            <Icon name="HelpCircle" size={20} className="text-indigo-400 mt-1 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-white mb-2">Interview Question</h4>
              <p className="text-gray-300">{currentQuestion?.question}</p>
            </div>
          </div>
        </div>

        {/* Answer Comparison Toggle */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-400">Score:</span>
              <span className={`text-lg font-bold ${getScoreColor(currentQuestion?.score)}`}>
                {currentQuestion?.score}/100
              </span>
            </div>
            {currentQuestion?.audioUrl && (
              <button
                onClick={handlePlayAudio}
                className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-gray-800/50 hover:bg-gray-700/50 text-gray-400 hover:text-white transition-all duration-200 border border-gray-700"
              >
                <Icon name={isPlaying ? "Pause" : "Play"} size={16} />
                <span className="text-sm">{currentQuestion?.duration}</span>
              </button>
            )}
          </div>
          <Button
            variant={showComparison ? "default" : "outline"}
            size="sm"
            onClick={() => setShowComparison(!showComparison)}
            iconName="GitCompare"
            className={showComparison 
              ? "bg-gradient-to-r from-purple-500 to-indigo-600" 
              : "border-gray-600 text-gray-300 hover:text-white"
            }
          >
            {showComparison ? 'Hide Comparison' : 'Compare Answers'}
          </Button>
        </div>

        {/* Answer Content */}
        <div className="space-y-6">
          {!showComparison ? (
            /* Single Answer View */
            <div className="space-y-4">
              <div className="p-4 bg-gray-800/20 rounded-lg border border-gray-700">
                <div className="flex items-center space-x-2 mb-3">
                  <Icon name="User" size={16} className="text-indigo-400" />
                  <span className="text-sm font-medium text-white">Your Answer</span>
                </div>
                <p className="text-gray-300 leading-relaxed">{currentQuestion?.userAnswer}</p>
              </div>

              {/* Highlights */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/20">
                  <div className="flex items-center space-x-2 mb-3">
                    <Icon name="CheckCircle" size={16} className="text-green-400" />
                    <span className="text-sm font-medium text-green-400">Strengths</span>
                  </div>
                  <ul className="space-y-2">
                    {currentQuestion?.highlights?.strengths?.map((strength, index) => (
                      <li key={index} className="flex items-start space-x-2 text-sm text-gray-300">
                        <Icon name="Check" size={12} className="text-green-400 mt-1 flex-shrink-0" />
                        <span>{strength}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="p-4 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
                  <div className="flex items-center space-x-2 mb-3">
                    <Icon name="AlertCircle" size={16} className="text-yellow-400" />
                    <span className="text-sm font-medium text-yellow-400">Areas for Improvement</span>
                  </div>
                  <ul className="space-y-2">
                    {currentQuestion?.highlights?.improvements?.map((improvement, index) => (
                      <li key={index} className="flex items-start space-x-2 text-sm text-gray-300">
                        <Icon name="ArrowRight" size={12} className="text-yellow-400 mt-1 flex-shrink-0" />
                        <span>{improvement}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ) : (
            /* Comparison View */
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="p-4 bg-gray-800/20 rounded-lg border border-gray-700">
                  <div className="flex items-center space-x-2 mb-3">
                    <Icon name="User" size={16} className="text-indigo-400" />
                    <span className="text-sm font-medium text-white">Your Answer</span>
                  </div>
                  <p className="text-gray-300 leading-relaxed text-sm">{currentQuestion?.userAnswer}</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/20">
                  <div className="flex items-center space-x-2 mb-3">
                    <Icon name="Sparkles" size={16} className="text-green-400" />
                    <span className="text-sm font-medium text-green-400">AI Model Answer</span>
                  </div>
                  <p className="text-gray-300 leading-relaxed text-sm whitespace-pre-line">
                    {currentQuestion?.aiModelAnswer}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-700">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSelectedQuestion(Math.max(0, selectedQuestion - 1))}
            disabled={selectedQuestion === 0}
            iconName="ChevronLeft"
            className="border-gray-600 text-gray-300 hover:text-white disabled:text-gray-600"
          >
            Previous
          </Button>

          <span className="text-sm text-gray-400">
            {selectedQuestion + 1} of {questions?.length}
          </span>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setSelectedQuestion(Math.min(questions?.length - 1, selectedQuestion + 1))}
            disabled={selectedQuestion === questions?.length - 1}
            iconName="ChevronRight"
            iconPosition="right"
            className="border-gray-600 text-gray-300 hover:text-white disabled:text-gray-600"
          >
            Next
          </Button>
        </div>
      </div>
    </SpotlightCard>
  );
};

export default AnswerReviewSection;
